import { Theme, css } from '@emotion/react';

export const rightContent = css`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

/**
 * Applies appropriate "trailing" styling to an item within the rightItems section of a search result
 * button. This may just be right margin, it may be a padding/border/right margin combo that acts
 * as a vertical separator, or it may be nothing at all.
 */
export const rightItemTrailingDecoration = (
  { colors }: Theme,
  {
    isLast,
    isWide,
    numRemsIfLastAndWide,
    useLightDivider,
  }: { isLast: boolean; isWide: boolean; numRemsIfLastAndWide: number; useLightDivider: boolean }
) =>
  isLast
    ? isWide // So that what we're laying out isn't not directly on top of the end-of-button arrow:
      ? css`
          padding-right: ${numRemsIfLastAndWide}rem;
        `
      : null
    : /* The notion here is that we create a vertical separator on a <span> with the border-right style,
       * and center it in the space to the right of the element using equal padding-right & margin-right.
       *
       * We need to make sure the element has height too, at least 18px. Or if element content
       * dictates it'll be taller, that's okay too. (18px matches what's shown in the
       * CHASE Exclusive case.) */
      css`
        border-right: 1px solid ${useLightDivider ? colors.borderContrast : colors.borderDefault};
        margin-right: 2rem;
        min-height: 18px;
        padding-right: 2rem;
      `;

export const chasePayExclusive = (
  theme: Theme,
  partialRightItemConfig: { isLast: boolean; isWide: boolean; useLightDivider: boolean }
) => [
  css`
    align-items: center;
    display: flex;
    ${theme.typography.variant('caption')};
  `,
  rightItemTrailingDecoration(theme, {
    ...partialRightItemConfig,
    numRemsIfLastAndWide: 1,
  }),
];

export const communalCountItem = (
  theme: Theme,
  partialRightItemConfig: { isLast: boolean; isWide: boolean; useLightDivider: boolean }
) => {
  const styles = [
    css`
      ${theme.typography.variant('caption')};
    `,
    rightItemTrailingDecoration(theme, {
      ...partialRightItemConfig,
      numRemsIfLastAndWide: 1,
    }),
  ];

  // Use lighter font color when we need a light divider
  if (partialRightItemConfig.useLightDivider) {
    styles.push(css`
      color: ${theme.colors.bgDefault};
    `);
  }

  return styles;
};

export const priceItem = ({ colors, typography }: Theme) =>
  // The price item, if it's there, is always last of all, and never has a trailing decoration applied to it.
  css`
    display: inline;
    color: ${colors.bgDefault};
    ${typography.variant('caption')};
  `;
